import { useEffect, useMemo, useState } from 'react'
import {
  listSupportAccessLog,
  type ListSupportAccessLogFilters,
  type SupportAccessLogEntry,
} from '../../../services/ownerConsoleService'
import { useNls } from '../../../nls/nlsProvider'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import { TextInput } from '../../Common/TextInput'
import { Select } from '../../Common/Select'
import './ownerActivity.css'

const PATH_CLASSES = [
  'WORKSPACE_DETAIL',
  'WORKSPACE_LIST',
  'PERSON_DETAIL',
  'PERSON_LIST',
  'COHORT_MEMBERS',
  'FEEDBACK_REPORTS',
  'DEBUG_REPORTS',
  'DEBUG_BUNDLE',
  'SUPPORT_TICKET',
  'SUPPORT_TICKET_LIST',
  'REPORTER_ACCOUNT',
  'TELEMETRY_LOGS',
  'CLIENT_OUTREACH',
  'DEMO_WORKSPACE',
] as const

const formatDate = (millis?: number) => {
  if (!millis) return '—'
  return new Date(millis).toLocaleString()
}

export const OwnerSupportAccessPanel = () => {
  const { t } = useNls()

  const [workspaceDraft, setWorkspaceDraft] = useState('')
  const [actorDraft, setActorDraft] = useState('')
  const [pathClassFilter, setPathClassFilter] = useState('ALL')

  const [activeFilters, setActiveFilters] = useState<ListSupportAccessLogFilters>({})
  const [rows, setRows] = useState<SupportAccessLogEntry[]>([])
  const [nextCursor, setNextCursor] = useState<{ id: string } | null>(null)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [reloadTrigger, setReloadTrigger] = useState(0)

  const helpItems: [string, string, string] = [
    t('NLSOwnerConsoleSupportAccessHelpOne'),
    t('NLSOwnerConsoleSupportAccessHelpTwo'),
    t('NLSOwnerConsoleSupportAccessHelpThree'),
  ]

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoadState('loading')
    setErrorMsg(null)
    let cancelled = false
    void listSupportAccessLog(activeFilters)
      .then((res) => {
        if (cancelled) return
        setRows(res.items)
        setNextCursor(res.nextCursor)
        setLoadState('ready')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : t('NLSOwnerConsoleSupportAccessLoadError')
        setErrorMsg(msg || t('NLSOwnerConsoleSupportAccessLoadError'))
        setLoadState('error')
      })
    return () => {
      cancelled = true
    }
  }, [activeFilters, reloadTrigger, t])

  const handleApplyFilters = () => {
    const filters: ListSupportAccessLogFilters = {}
    if (workspaceDraft.trim()) filters.workspaceId = workspaceDraft.trim()
    if (actorDraft.trim()) filters.actorUid = actorDraft.trim()
    if (pathClassFilter !== 'ALL') filters.pathClass = pathClassFilter
    setActiveFilters(filters)
  }

  const handleResetFilters = () => {
    setWorkspaceDraft('')
    setActorDraft('')
    setPathClassFilter('ALL')
    setActiveFilters({})
  }

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const res = await listSupportAccessLog(activeFilters, nextCursor)
      setRows((prev) => [...prev, ...res.items])
      setNextCursor(res.nextCursor)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('NLSOwnerConsoleSupportAccessLoadError')
      setErrorMsg(msg || t('NLSOwnerConsoleSupportAccessLoadError'))
    } finally {
      setLoadingMore(false)
    }
  }

  const columns: ColumnDef<SupportAccessLogEntry>[] = useMemo(() => [
    {
      key: 'time',
      header: t('NLSOwnerConsoleSupportAccessColTimestamp'),
      render: (row) => formatDate(row.createdAtMillis),
      width: 180,
    },
    {
      key: 'actor',
      header: t('NLSOwnerConsoleSupportAccessColActor'),
      render: (row) => (
        <span title={row.actorUid}>
          {row.actorEmail || row.actorUid} ({row.actorRole})
        </span>
      ),
      width: 200,
    },
    {
      key: 'pathClass',
      header: t('NLSOwnerConsoleSupportAccessColPathClass'),
      render: (row) => <code>{row.pathClass}</code>,
      width: 180,
    },
    {
      key: 'callable',
      header: t('NLSOwnerConsoleSupportAccessColCallable'),
      render: (row) => row.callable,
      width: 180,
    },
    {
      key: 'workspaceId',
      header: t('NLSOwnerConsoleSupportAccessColWorkspace'),
      render: (row) => row.workspaceId || '—',
      width: 140,
    },
    {
      key: 'target',
      header: t('NLSOwnerConsoleSupportAccessColTarget'),
      render: (row) => row.targetId || row.subjectUid || '—',
      width: 140,
    },
    {
      key: 'reason',
      header: t('NLSOwnerConsoleSupportAccessColReason'),
      render: (row) => {
        if (!row.reason) return '—'
        return row.reason.linkedId ? `${row.reason.code} (${row.reason.linkedId})` : row.reason.code
      },
      width: 160,
    },
    {
      key: 'itemCount',
      header: t('NLSOwnerConsoleSupportAccessColItems'),
      render: (row) => (typeof row.itemCount === 'number' ? String(row.itemCount) : '—'),
      width: 80,
    },
  ], [t])

  return (
    <section className="ownerActivityPanel" aria-labelledby="support-access-title">
      <header className="ownerActivityHeader">
        <div>
          <h1 id="support-access-title">{t('NLSOwnerConsoleSupportAccessTitle')}</h1>
          <p className="ownerActivitySubtitle">{t('NLSOwnerConsoleSupportAccessSubtitle')}</p>
        </div>
      </header>

      <div className="ownerActivityLayout">
        <aside className="ownerActivitySidebar" aria-label="Filters">
          <div>
            <span>{t('NLSOwnerConsoleSupportAccessFilterWorkspace')}</span>
            <TextInput
              aria-label={t('NLSOwnerConsoleSupportAccessFilterWorkspace')}
              placeholder={t('NLSOwnerConsoleSupportAccessFilterWorkspace')}
              value={workspaceDraft}
              onChange={setWorkspaceDraft}
            />
          </div>

          <div>
            <span>{t('NLSOwnerConsoleSupportAccessFilterActor')}</span>
            <TextInput
              aria-label={t('NLSOwnerConsoleSupportAccessFilterActor')}
              placeholder={t('NLSOwnerConsoleSupportAccessFilterActor')}
              value={actorDraft}
              onChange={setActorDraft}
            />
          </div>

          <div>
            <span>{t('NLSOwnerConsoleSupportAccessFilterPathClass')}</span>
            <Select
              ariaLabel={t('NLSOwnerConsoleSupportAccessFilterPathClass')}
              value={pathClassFilter}
              onChange={setPathClassFilter}
              options={[
                { value: 'ALL', label: t('NLSOwnerConsoleSupportAccessAllPathClasses') },
                ...PATH_CLASSES.map((pc) => ({ value: pc, label: pc })),
              ]}
            />
          </div>

          <button
            type="button"
            className="ownerActivityButton"
            onClick={handleApplyFilters}
          >
            {t('NLSOwnerConsoleSupportAccessApplyFilters')}
          </button>
          <button
            type="button"
            className="ownerActivityButton"
            onClick={handleResetFilters}
          >
            {t('NLSOwnerConsoleSupportAccessResetFilters')}
          </button>
        </aside>

        <div className="ownerActivityList">
          {loadState === 'loading' ? (
            <div className="ownerActivityState" role="status">
              {t('NLSOwnerConsoleSupportAccessLoading')}
            </div>
          ) : loadState === 'error' ? (
            <OwnerEmptyScaffold
              title={t('NLSOwnerConsoleSupportAccessLoadError')}
              body={errorMsg ?? t('NLSOwnerConsoleSupportAccessLoadError')}
              howDataGetsHere={helpItems}
              configLabel={t('NLSOwnerConsoleSupportAccessRetry')}
              onConfigClick={() => setReloadTrigger((prev) => prev + 1)}
            />
          ) : (
            <>
              <OwnerTable
                data={rows}
                columns={columns}
                viewKey="owner-support-access"
                getRowId={(entry) => entry.id}
                pageSize={Math.max(1, rows.length)}
                emptyState={
                  <OwnerEmptyScaffold
                    title={t('NLSOwnerConsoleSupportAccessEmptyTitle')}
                    body={t('NLSOwnerConsoleSupportAccessEmptyBody')}
                    howDataGetsHere={helpItems}
                  />
                }
              />
              {errorMsg ? (
                <div className="ownerActivityInlineError" role="alert">
                  {errorMsg}
                </div>
              ) : null}
              {nextCursor ? (
                <button
                  type="button"
                  className="ownerActivityButton ownerActivityLoadMore"
                  disabled={loadingMore}
                  onClick={() => void handleLoadMore()}
                >
                  {loadingMore
                    ? t('NLSOwnerConsoleSupportAccessLoading')
                    : t('NLSOwnerConsoleSupportAccessLoadMore')}
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </section>
  )
}
